import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { io } from 'socket.io-client'
import { MetroSpinner } from 'react-spinners-kit'

import { refreshToken } from '../../actions/auth'
import { updateConfirmationStatus } from '../../actions/confirmation'
import Menu from '../../components/menus/Menu/Menu'
import Footer from '../../components/Footer/Footer'
import CampaignTable from '../../components/tables/CampaignTable/CampaignTable'
import clients from '../../constants/clients'
import { useTitle } from '../../helpers/use_title.helper'
import { countResponseStatus, formatContact, formatGuardian, formatRowStatus } from '../../helpers/table.helper'
import { formatDate, formatHeader, strToDate, strToTime } from '../../helpers/style_string.helper'
import { reorderJsonTable } from '../../helpers/excel_json.helper'
import './Main.css'

function Main () {
  useTitle('Ki | Confirmación')
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)
  const client = useSelector((state) => state.client)
  const token = useSelector((state) => state.token)

  const [fileName, setFileName] = useState('')
  const [json, setJson] = useState([])
  const [header, setHeader] = useState([])
  const [loadingTable, setLoadingTable] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [dateForDB, setDateForDB] = useState(formatDate(new Date()))
  const [campaignState, setCampaignState] = useState('')
  const [campaignState2, setCampaignState2] = useState('')
  const [tableState, setTableState] = useState({})
  const [lastChange, setLastChange] = useState({})
  const [flowStates, setFlowStates] = useState({
    sent: 0,
    delivered: 0,
    read: 0,
    answered: 0,
    confirmed: 0,
    cancelled: 0,
    call: 0,
    other: 0,
    noReply: 0,
    notAttended: 0,
    rescheduled: 0,
    patientWillCall: 0
  })
  const [filter, setFilter] = useState({
    baseTable: 'all'
  })
  const [savingMessagesStatus, setSavingMessagesStatus] = useState('')
  const [states, setStates] = useState([])

  // WebSocket connection
  const socketRef = useRef(null)
  const events = useRef([])
  const isHistory = useRef(false)
  const isRefreshingToken = useRef(false)

  async function handleTokenRefresh () {
    // Prevent multiple simultaneous token refresh attempts
    if (isRefreshingToken.current) {
      // console.log('Already refreshing token, skipping...')
      return
    }

    isRefreshingToken.current = true

    try {
      // console.log('Token expired, attempting to refresh...')
      // console.log('Old token preview:', token ? `${token.substring(0, 20)}...` : 'NO_TOKEN')
      const res = await refreshToken(token)
      if (res.data?.token) {
        const newToken = res.data.token
        // console.log('New token received, preview:', `${newToken.substring(0, 20)}...`)

        // Update token in Redux and localStorage
        window.localStorage.setItem('token', JSON.stringify(newToken))
        dispatch({
          type: 'UPDATED_TOKEN',
          payload: newToken
        })

        // Update socket auth with new token and reconnect
        if (socketRef.current) {
          // Update the socket's auth options with the new token
          socketRef.current.io.opts.auth = { token: newToken }
          // Reconnect using the existing socket
          socketRef.current.connect()
          // console.log('Token refreshed, reconnecting with existing socket...')
        } else {
          // Fallback: create new connection with new token
          connectWebSocket(newToken)
        }
      } else {
        throw new Error('No new token received')
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      toast.error('Error de autenticación en la conexión en tiempo real')
    } finally {
      // Reset flag even if token refresh fails
      isRefreshingToken.current = false
    }
  }

  // WebSocket connection function
  async function connectWebSocket (customToken = null) {
    const serverUrl = process.env.REACT_APP_WEBSOCKET_URL
    const tokenToUse = customToken || token

    // console.log('Connecting WebSocket with token:', customToken ? 'CUSTOM_TOKEN' : 'REDUX_TOKEN')
    // console.log('Token preview:', tokenToUse ? `${tokenToUse.substring(0, 20)}...` : 'NO_TOKEN')

    // Connect with authentication token
    socketRef.current = io(serverUrl, {
      auth: { token: tokenToUse },
      transports: ['websocket', 'polling'], // Fallback to polling if WebSocket fails
      withCredentials: false, // Try without credentials first
      timeout: 20000, // Increase timeout for production
      forceNew: true // Force new connection
    })

    // Handle connection success
    socketRef.current.on('connect', () => {
      console.log('WebSocket connected successfully')
      // Reset flag on successful connection
      isRefreshingToken.current = false
      // Start watching status updates
      socketRef.current?.emit('watchStatusUpdates')
    })

    // Handle connection errors (including auth failures)
    socketRef.current.on('connect_error', async (err) => {
      console.error('WebSocket connection error:', err.message)
      console.error('Error details:', err)
      console.error('Server URL:', serverUrl)
      console.error('Token available:', !!tokenToUse)

      if (err?.message === 'Invalid authentication token' || err?.message === 'UNAUTHORIZED' || err?.message === 'TOKEN_EXPIRED') {
        await handleTokenRefresh()
      } else {
        toast.error('Error de conexión en tiempo real')
      }
    })

    socketRef.current.on('disconnect', async (reason) => {
      console.log('WebSocket disconnected:', reason)

      // Handle server-initiated disconnects (likely due to expired token)
      if (reason === 'io server disconnect') {
        console.log('Server disconnected us, likely expired token')
        await handleTokenRefresh()
      }
      // For other disconnects, let the connect_error handler deal with reconnection
    })

    socketRef.current.on('watchingStatusUpdates', (data) => {})

    // Handle real-time status updates
    socketRef.current.on('statusUpdate', (data) => {
      if (data.type === 'statusChange' && data.confirmationId) {
        // Create a mock change object similar to what Realm provided
        const mockChange = {
          operationType: 'update',
          fullDocument: {
            _id: data.confirmationId,
            status: data.status,
            timestamp: data.timestamp
            // Add other fields if needed
          }
        }

        // Check if we already have a recent update for this confirmation
        const existingEvent = events.current.find(event =>
          event.fullDocument._id === mockChange.fullDocument._id
        )

        if (!existingEvent) {
          // First time seeing this confirmation, add it
          setLastChange(mockChange.fullDocument)
          events.current = [...events.current, mockChange]
        } else {
          // Update existing event with new status
          existingEvent.fullDocument.status = mockChange.fullDocument.status
          existingEvent.fullDocument.timestamp = mockChange.fullDocument.timestamp
          // Update lastChange to trigger UI update
          setLastChange(mockChange.fullDocument)
        }
      }
    })

    socketRef.current.on('error', (error) => {
      console.error('WebSocket error:', error)
      toast.error('Error en la conexión en tiempo real')
    })
  }

  // Initialize WebSocket connection
  useEffect(() => {
    let isActive = true
    let connectionCheckInterval

    try {
      if (isActive && tableState && Object.keys(tableState).length > 0) {
        connectWebSocket()

        // Set up periodic connection check
        connectionCheckInterval = setInterval(() => {
          if (socketRef.current && !socketRef.current.connected && !isRefreshingToken.current) {
            console.log('WebSocket not connected, attempting to reconnect...')
            connectWebSocket()
          }
        }, 30000) // Check every 30 seconds
      }
    } catch (error) {
      console.log('WebSocket connection error:', error)
      toast.error('Error al conectar con el servidor en tiempo real')
    }

    return function cleanup () {
      isActive = false
      // Clean up WebSocket connection
      if (socketRef.current) {
        socketRef.current.emit('stopWatchingStatusUpdates')
        socketRef.current.disconnect()
        socketRef.current = null
      }
      // Clear the interval
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval)
      }
    }
  }, [tableState])

  // Note: Removed token change effect to prevent reconnection loops
  // The WebSocket will reconnect automatically when the token is refreshed

  // Handle status changes (same logic as before)
  useEffect(() => {
    let isActive = true

    if (isActive) {
      if (Object.keys(tableState).includes('_id') &&
        lastChange &&
        Object.keys(lastChange).length > 0
      ) {
        setTableState(prevTable => {
          const confirmations = prevTable.confirmations
          confirmations.forEach(c => {
            if (c._id === lastChange._id) {
              c.status = lastChange.status
              c.wpStatus = lastChange.wpStatus
            }
          })
          // Reorder json array
          updateLocalStorage(prevTable, 'table', 'NEW_TABLE')
          const campaign = campaignState === '' ? campaignState2 : campaignState
          formatJson(confirmations, campaign, true, campaignState2 === '6 month', false)
          return prevTable
        })
      }
    }

    return function cleanup () {
      isActive = false
    }
  }, [lastChange])

  async function onWpStateChange (e, confirmationId) {
    try {
      const value = e.target.value + '-' + user._id
      const payload = { status: value, confirmationId }
      await updateConfirmationStatus(user, token, payload)
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) toast.error(error.response.data.error || error.response.data.message)
      }
    }
  }

  function reset () {
    setFlowStates({
      sent: 0,
      delivered: 0,
      read: 0,
      answered: 0,
      confirmed: 0,
      cancelled: 0,
      call: 0,
      other: 0,
      noReply: 0,
      notAttended: 0,
      rescheduled: 0,
      patientWillCall: 0
    })
    setJson([])
    setFileName('')
    setHeader([])
    setSavingMessagesStatus('')
    updateLocalStorage(null, 'table', 'CLEAR_TABLE')
    resetFilters()
    isHistory.current = false
  }

  function resetFilters () {
    setFilter({ baseTable: 'all' })
  }

  function updateLocalStorage (data, objToUpdate, type) {
    window.localStorage.setItem(objToUpdate, JSON.stringify(data))
    dispatch({
      type,
      payload: data
    })
  }

  function formatJson (confirmations, campaign, stateAsSelector = true, is6Month = false, changeHeader = false, format = true) {
    if (confirmations.length > 0 && campaign !== '') {
      let formattedJson = confirmations
      if (format) {
        formattedJson = confirmations.map(({ updatedAt, createdAt, __v, ...row }) => {
          const r = {
            ...row,
            date: strToDate(row.date),
            time: strToTime(row.time),
            status: formatRowStatus(row.status, campaign, client._id)
          }
          if (client._id === clients.JUAN_PABLO_GONZALEZ && campaign === 'marketing') {
            delete r.guardian
            delete r.time
            delete r.name
          }
          if (Object.keys(row).includes('guardian')) r.guardian = formatGuardian(row.guardian)
          if (r.contact) r.contact = formatContact(row.contact)
          return r
        })
      }
      const orderedJson = reorderJsonTable(formattedJson, client._id, user, isHistory, stateAsSelector, is6Month, campaign)
      setJson(orderedJson)
      if (changeHeader) setHeader(formatHeader(orderedJson, client, campaign))
      countResponseStatus(confirmations, campaign, client._id, setFlowStates)
    }
  }

  return (
    <div
      className={`main ${(isHistory.current ? campaignState2 : campaignState) === 'scheduling' ? 'bg-color-2' : ''}`}
    >
      <div className='main-container'>
        <div className='row main-container-row'>
          {(window.outerWidth > 1028 || json.length === 0) &&
            <Menu
              fileName={fileName}
              loadingTable={loadingTable}
              setDateForDB={setDateForDB}
              campaignState={campaignState}
              setCampaignState={setCampaignState}
              campaignState2={campaignState2}
              setCampaignState2={setCampaignState2}
              dateForDB={dateForDB}
              setTableState={setTableState}
              reset={reset}
              formatJson={formatJson}
              setJson={setJson}
              setHeader={setHeader}
              setFileName={setFileName}
              isHistory={isHistory}
              setLoadingTable={setLoadingTable}
              setStates={setStates}
              updateLocalStorage={updateLocalStorage}
              setIsLoading={setIsLoading}
            />}
          {(window.outerWidth > 1028 || json.length > 0) &&
            <div className='col-md-9 information'>
              <div className='row'>
                <div className='information-container'>
                  {window.outerWidth < 1028 &&
                    <button
                      onClick={() => {
                        setCampaignState2('')
                        reset()
                      }}
                      className='back-btn'
                    >
                      <i className='fas fa-arrow-left' />
                    </button>}
                  {isLoading === true
                    ? (
                      <div className='loading-container'>
                        <div className='loading'>
                          <div className='loading-animation'>
                            <MetroSpinner size={70} color='#304044' loading={isLoading} />
                          </div>
                        </div>
                      </div>
                      )
                    : json.length > 0 &&
                      <CampaignTable
                        json={json}
                        header={header}
                        tableState={tableState}
                        flowStates={flowStates}
                        isHistory={isHistory}
                        onWpStateChange={onWpStateChange}
                        campaignState={campaignState}
                        campaignState2={campaignState2}
                        fileName={fileName}
                        savingMessagesStatus={savingMessagesStatus}
                        setSavingMessagesStatus={setSavingMessagesStatus}
                        updateLocalStorage={updateLocalStorage}
                        setTableState={setTableState}
                        setFlowStates={setFlowStates}
                        states={states}
                        filter={filter}
                        resetFilters={resetFilters}
                      />}
                </div>

                <Footer />
              </div>
            </div>}
        </div>
      </div>
    </div>
  )
}

export default Main
